import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

const SESSION_URL_PARAM = 'session';

const useSessionUrlSync = ({ ready, openSessionId, onOpenFromUrl, onUrlCleared, pauseStateSync = false }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlSessionId = searchParams.get(SESSION_URL_PARAM);
    const initializedRef = useRef(false);

    const openSessionIdRef = useRef(openSessionId);
    useEffect(() => {
        openSessionIdRef.current = openSessionId;
    }, [openSessionId]);

    const onOpenFromUrlRef = useRef(onOpenFromUrl);
    onOpenFromUrlRef.current = onOpenFromUrl;
    const onUrlClearedRef = useRef(onUrlCleared);
    onUrlClearedRef.current = onUrlCleared;

    const setSessionParam = useCallback(
        sessionId => {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev);
                    if (sessionId) next.set(SESSION_URL_PARAM, sessionId);
                    else next.delete(SESSION_URL_PARAM);
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    // URL → state: on initial load open the URL session, and react to back/forward navigation.
    useEffect(() => {
        if (!ready) return;
        const wasInitialized = initializedRef.current;
        initializedRef.current = true;
        if ((urlSessionId || null) === (openSessionIdRef.current || null)) return;
        if (urlSessionId) {
            onOpenFromUrlRef.current(urlSessionId);
        } else if (wasInitialized) {
            onUrlClearedRef.current();
        }
    }, [urlSessionId, ready]);

    // state → URL: mirror the open session into the query param.
    useEffect(() => {
        if (!initializedRef.current || pauseStateSync) return;
        if ((urlSessionId || null) === (openSessionId || null)) return;
        setSessionParam(openSessionId);
    }, [openSessionId, urlSessionId, pauseStateSync, setSessionParam]);

    return { urlSessionId, setSessionParam };
};

export default useSessionUrlSync;
